import type { Space } from "@circle-react/types/Space";
/**
 * Single bridge to legacy `StandardLayout/*` (V1) and `StandardLayoutV2/*`
 * (V2) components while the member-area AppShell FF
 * (`circle_v4_member_area_appshell_enabled`) rolls out. FF off → each
 * public name renders its V1/V2 component; FF on → renders the
 * WorkspacePage impl in `../WorkspacePage/components/`.
 *
 * Cleanup at FF=100%: first rename remaining legacy-alias consumers to
 * canonical names: `PageMainContent` → `PageContent`, `PageBreadcrumb` →
 * `Breadcrumb`, `StandardLayoutHeader` → `PageHeader`, and
 * `StandardLayoutHeaderWithPortal` → `PageHeaderWithPortal`. Other aliases
 * must likewise use their canonical `./components/*` exports. Then delete
 * this file; in `WorkspacePage/index.tsx` replace the `from "../LegacyBridge"`
 * block with direct re-exports from `./components/*`, removing `PageRoot`
 * as described below. Run `pnpm fallow` to surface orphan V1/V2 files.
 */
import type { ComponentPropsWithoutRef, ReactNode } from "react";

import { Body as V1Body } from "@/react/components/Layout/StandardLayout/Body";
import { Content as V1Content } from "@/react/components/Layout/StandardLayout/Content";
import { Right as V1Right } from "@/react/components/Layout/StandardLayout/Right";
import {
  StandardLayoutV2 as V2Root,
  type StandardLayoutV2Props,
} from "@/react/components/Layout/StandardLayoutV2";
import { Body as V2Body } from "@/react/components/Layout/StandardLayoutV2/Body";
import { Breadcrumb as V2Breadcrumb } from "@/react/components/Layout/StandardLayoutV2/Breadcrumb";
import { Content as V2Content } from "@/react/components/Layout/StandardLayoutV2/Content";
import { CoverImageLoader as V2CoverImageLoader } from "@/react/components/Layout/StandardLayoutV2/CoverImageLoader";
import { HomeCoverForm as V2HomeCoverForm } from "@/react/components/Layout/StandardLayoutV2/HomeCoverForm";
import { RailBarPortal as V2RailBarPortal } from "@/react/components/Layout/StandardLayoutV2/RailBarPortal";
import { SpaceCoverForm as V2SpaceCoverForm } from "@/react/components/Layout/StandardLayoutV2/SpaceCoverForm";
import { StandardLayoutHeader as V2Header } from "@/react/components/Layout/StandardLayoutV2/StandardLayoutHeader";
import { StandardLayoutHeaderWithPortal as V2HeaderWithPortal } from "@/react/components/Layout/StandardLayoutV2/StandardLayoutHeader/StandardLayoutHeaderWithPortal";
import { StandardLayoutHeaderContainer as V2HeaderContainer } from "@/react/components/Layout/StandardLayoutV2/StandardLayoutHeaderContainer";
import { useIsMemberAreaAppShellEnabled } from "@/react/hooks/useIsMemberAreaAppShellEnabled";

import {
  PageBody as WorkspacePageBody,
  type BodyProps,
} from "../WorkspacePage/components/Body";
import {
  Breadcrumb as WorkspaceBreadcrumb,
  type BreadcrumbProps,
} from "../WorkspacePage/components/Breadcrumb";
import {
  PageContent as WorkspacePageContent,
  type ContentProps,
} from "../WorkspacePage/components/Content";
import {
  CoverImageLoader as WorkspaceCoverImageLoader,
  type CoverImageLoaderProps,
} from "../WorkspacePage/components/CoverImage/CoverImageLoader";
import { HomeCoverForm as WorkspaceHomeCoverForm } from "../WorkspacePage/components/CoverImage/HomeCoverForm";
import { SpaceCoverForm as WorkspaceSpaceCoverForm } from "../WorkspacePage/components/CoverImage/SpaceCoverForm";
import {
  PageHeader as WorkspaceHeader,
  type PageHeaderProps,
} from "../WorkspacePage/components/Header/Header";
import { PageHeaderContainer as WorkspaceHeaderContainer } from "../WorkspacePage/components/Header/HeaderContainer";
import { PageHeaderWithPortal as WorkspaceHeaderWithPortal } from "../WorkspacePage/components/Header/HeaderWithPortal";
import { RailBarPortal as WorkspaceRailBarPortal } from "../WorkspacePage/components/Railbar/RailBarPortal";
import {
  PageRightRail as WorkspacePageRightRail,
  type RightProps,
} from "../WorkspacePage/components/Right";

export const PageBody = (props: BodyProps) => {
  const isFFOn = useIsMemberAreaAppShellEnabled();
  return isFFOn ? <WorkspacePageBody {...props} /> : <V1Body {...props} />;
};

// V2-flavored variant for `pages/Profile`: Profile's pre-AppShell chrome
// (`V2.Body`, no gutters) intentionally differed from V1.Body
// (`sm:mx-6 md:pt-6 lg:mx-9`); those insets look wrong against Profile's
// full-width split layout. On-FF renders bare since `WorkspaceBackground`
// + Profile's own `PageContent` cover background/sizing.
export const ProfilePageBody = ({ children }: { children: ReactNode }) => {
  const isFFOn = useIsMemberAreaAppShellEnabled();
  return isFFOn ? <>{children}</> : <V2Body>{children}</V2Body>;
};

export const PageRightRail = (props: RightProps) => {
  const isFFOn = useIsMemberAreaAppShellEnabled();
  return isFFOn ? (
    <WorkspacePageRightRail {...props} />
  ) : (
    <V1Right {...props} />
  );
};

export const PageContent = (props: ContentProps) => {
  const isFFOn = useIsMemberAreaAppShellEnabled();
  return isFFOn ? (
    <WorkspacePageContent {...props} />
  ) : (
    <V2Content {...(props as ComponentPropsWithoutRef<"div">)} />
  );
};

// V1-flavored variant of `PageContent` for ex-`StandardLayout.Content` callers
// (centered `mx-auto h-full max-w-5xl` div). On-FF resolves to WorkspacePage's
// `PageContent` (also centered max-w-5xl by default) in
// `WorkspacePage/components/Content.tsx`. Use `PageContent` when callers need
// V2-only props (`fullWidth` / `fullHeight` / `noPadding`).
export const PageMainContent = (props: {
  children: ReactNode;
  className?: string;
}) => {
  const isFFOn = useIsMemberAreaAppShellEnabled();
  return isFFOn ? (
    <WorkspacePageContent {...props} />
  ) : (
    <V1Content {...props} />
  );
};

export const Breadcrumb = (props: BreadcrumbProps) => {
  const isFFOn = useIsMemberAreaAppShellEnabled();
  return isFFOn ? (
    <WorkspaceBreadcrumb {...props} />
  ) : (
    <V2Breadcrumb {...props} />
  );
};

export const PageBreadcrumb = Breadcrumb;

const PageHeader = (props: PageHeaderProps) => {
  const isFFOn = useIsMemberAreaAppShellEnabled();
  return isFFOn ? <WorkspaceHeader {...props} /> : <V2Header {...props} />;
};

export const StandardLayoutHeader = PageHeader;

export const PageHeaderContainer = (
  props: ComponentPropsWithoutRef<"header">,
) => {
  const isFFOn = useIsMemberAreaAppShellEnabled();
  return isFFOn ? (
    <WorkspaceHeaderContainer {...props} />
  ) : (
    <V2HeaderContainer {...props} />
  );
};

const PageHeaderWithPortal = (props: PageHeaderProps) => {
  const isFFOn = useIsMemberAreaAppShellEnabled();
  return isFFOn ? (
    <WorkspaceHeaderWithPortal {...props} />
  ) : (
    <V2HeaderWithPortal {...props} />
  );
};

export const StandardLayoutHeaderWithPortal = PageHeaderWithPortal;

export const CoverImageLoader = (props: CoverImageLoaderProps) => {
  const isFFOn = useIsMemberAreaAppShellEnabled();
  return isFFOn ? (
    <WorkspaceCoverImageLoader {...props} />
  ) : (
    <V2CoverImageLoader {...props} />
  );
};

export const HomeCoverForm = () => {
  const isFFOn = useIsMemberAreaAppShellEnabled();
  return isFFOn ? <WorkspaceHomeCoverForm /> : <V2HomeCoverForm />;
};

export const SpaceCoverForm = ({ space }: { space: Space }) => {
  const isFFOn = useIsMemberAreaAppShellEnabled();
  return isFFOn ? (
    <WorkspaceSpaceCoverForm space={space} />
  ) : (
    <V2SpaceCoverForm space={space} />
  );
};

interface RailBarPortalProps {
  children: ReactNode;
  isFullWidth?: boolean;
  isLoading?: boolean;
  loaderVariant?: "rail-profile" | "rail-member-list";
  noBorderLeft?: boolean;
  position?: "header" | "content" | "portal" | "drawer";
}

export const RailBarPortal = (props: RailBarPortalProps) => {
  const isFFOn = useIsMemberAreaAppShellEnabled();
  return isFFOn ? (
    <WorkspaceRailBarPortal {...props} />
  ) : (
    <V2RailBarPortal {...props} />
  );
};

/*
 * Root-level FF gate for pages that own their full page chrome via the
 * V1/V2 root layout when the AppShell is off. When the AppShell FF is on
 * the shell already provides sidebar/appbar/content frame, so this bridge
 * collapses to just rendering its children. The bridge centralizes the
 * `isMemberAreaAppShellEnabled ? content : <StandardLayoutV2 ...>{content}`
 * pattern that was previously repeated in every page entry under
 * `app/javascript/react/pages/`.
 *
 * When the AppShell FF reaches 100%, delete this export — every consumer
 * becomes `<>{children}</>` and the wrapper falls out naturally per the
 * cleanup recipe at the top of this file.
 */
export const PageRoot = ({ children, ...props }: StandardLayoutV2Props) => {
  const isFFOn = useIsMemberAreaAppShellEnabled();
  if (isFFOn) return <>{children}</>;
  return <V2Root {...props}>{children}</V2Root>;
};
